import { useCallback, useEffect, useState } from 'react'
import {
  createProspectCampaign,
  getProspectCampaign,
  saveProspectCampaign,
  subscribeProspects,
  TOUCH_CHANNEL_LABEL,
  TOUCH_OUTCOME_LABEL,
  type PersonaView,
  type ProspectCampaignDetail,
  type RemoteProspect,
  type RemoteProspectCampaign
} from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { Card, Pill, StyleIssues, inputCls, selectCls, useStyleCheck, when } from './ui'

// Campaigns: a group of people from one persona, worked toward one goal, with
// the messaging for that group and a record of everything sent as part of it.
// The stages count what was actually logged, so a campaign never reports a
// reach it did not have.

const STATUS_TONE: Record<string, 'accent' | 'muted' | 'warn'> = { active: 'accent', draft: 'muted', paused: 'warn', archived: 'muted' }

export function Campaigns({
  campaigns,
  personas,
  people,
  onChanged,
  onOpenPerson
}: {
  campaigns: RemoteProspectCampaign[]
  personas: PersonaView[]
  people: RemoteProspect[]
  onChanged: () => void
  onOpenPerson: (id: string) => void
}): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [selected, setSelected] = useState<string | null>(campaigns[0]?.id ?? null)
  const [name, setName] = useState('')
  const [personaId, setPersonaId] = useState('')
  const [err, setErr] = useState<string | null>(null)

  async function create(): Promise<void> {
    setErr(null)
    const r = await createProspectCampaign({ name: name.trim(), channel: 'linkedin', personaId: personaId || null })
    if (!r.ok) return setErr(r.error)
    setName('')
    onChanged()
    setSelected(r.data.campaign.id)
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[18rem_1fr] gap-4 items-start">
      <div className="glass-card rounded-xl p-3 space-y-1" data-testid="campaigns-list">
        {campaigns.map((c) => (
          <button key={c.id} onClick={() => setSelected(c.id)} className={`w-full text-left rounded-md px-3 py-2 ${selected === c.id ? 'bg-accent/10' : 'hover:bg-white/[0.03]'}`} data-testid={`campaign-row-${c.id}`}>
            <div className="text-sm font-medium flex items-center justify-between gap-2">
              <span className="truncate">{c.name}</span>
              <Pill tone={STATUS_TONE[c.status] ?? 'muted'}>{c.status}</Pill>
            </div>
            <div className="text-[11px] text-slate-500">{c.subscribed} people</div>
          </button>
        ))}
        {campaigns.length === 0 && <div className="text-sm text-slate-500 px-3 py-2">No campaigns yet.</div>}
        {canWrite && (
          <div className="pt-2 mt-2 border-t border-white/5 space-y-2">
            <input className={inputCls} placeholder="New campaign name" value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-new-name" />
            <select className={`${selectCls} w-full`} value={personaId} onChange={(e) => setPersonaId(e.target.value)} data-testid="campaign-new-persona">
              <option value="">For which persona?</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button className="btn-primary w-full justify-center" style={{ display: 'flex' }} disabled={!name.trim()} onClick={() => void create()} data-testid="campaign-create">
              Add campaign
            </button>
            {err && <div className="text-xs text-red-300">{err}</div>}
          </div>
        )}
      </div>
      {selected ? (
        <CampaignDetailView key={selected} id={selected} personas={personas} people={people} canWrite={canWrite} onChanged={onChanged} onOpenPerson={onOpenPerson} />
      ) : (
        <div className="glass-card rounded-xl p-8 text-sm text-slate-400">Add a campaign to group people and track what was sent to them.</div>
      )}
    </div>
  )
}

function CampaignDetailView({
  id,
  personas,
  people,
  canWrite,
  onChanged,
  onOpenPerson
}: {
  id: string
  personas: PersonaView[]
  people: RemoteProspect[]
  canWrite: boolean
  onChanged: () => void
  onOpenPerson: (id: string) => void
}): JSX.Element {
  const [c, setC] = useState<ProspectCampaignDetail | null>(null)
  const [draft, setDraft] = useState<Record<string, string | number | null>>({})
  const [err, setErr] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const load = useCallback(async () => {
    const r = await getProspectCampaign(id)
    if (!r.ok) return setErr(r.error)
    setC(r.data.campaign)
  }, [id])
  useEffect(() => void load(), [load])
  const v = (k: keyof ProspectCampaignDetail): string => String(draft[k] ?? (c?.[k] as string | number | null) ?? '')
  const noteIssues = useStyleCheck(v('noteTemplate'), 'note', '')
  const dmIssues = useStyleCheck(v('dmTemplate'), 'dm', '')
  if (!c) return <Card><div className="text-sm text-slate-400">{err ?? 'Loading…'}</div></Card>

  const memberIds = new Set(c.members.map((m) => m.id))
  const fromPersona = c.personaId ? people.filter((p) => p.personaId === c.personaId && !memberIds.has(p.id) && !p.doNotSend) : []

  async function save(): Promise<void> {
    setErr(null)
    const r = await saveProspectCampaign(id, draft)
    if (!r.ok) return setErr(r.error)
    setDraft({})
    setNotice('Saved.')
    await load()
    onChanged()
  }
  async function addFromPersona(): Promise<void> {
    const r = await subscribeProspects(id, fromPersona.map((p) => p.id))
    if (!r.ok) return setErr(r.error)
    setNotice(`Added ${r.data.subscribed}.${r.data.refused.length ? ` ${r.data.refused.length} refused (held back).` : ''}`)
    await load()
    onChanged()
  }

  const top = Math.max(1, c.stages[0]?.count ?? 1)
  return (
    <div className="space-y-4 min-w-0" data-testid="campaign-detail">
      <div className="glass-card rounded-xl p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input className="bg-transparent text-xl font-semibold focus:outline-none border-b border-transparent focus:border-accent flex-1 min-w-[12rem]" value={v('name')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="campaign-name" />
          <select className={selectCls} value={v('status')} disabled={!canWrite} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="campaign-status">
            {['draft', 'active', 'paused', 'archived'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <label className="text-xs text-slate-400">
            Persona
            <select className={`${selectCls} w-full mt-1`} value={v('personaId')} disabled={!canWrite} onChange={(e) => setDraft({ ...draft, personaId: e.target.value || null })} data-testid="campaign-persona">
              <option value="">None</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-400 sm:col-span-2">
            Goal
            <input className={`${inputCls} mt-1`} placeholder="e.g. ten real conversations with agency owners this month" value={v('goal')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, goal: e.target.value })} data-testid="campaign-goal" />
          </label>
        </div>
        {notice && <div className="text-xs text-emerald-300">{notice}</div>}
        {err && <div className="text-xs text-red-300">{err}</div>}
      </div>

      <Card title="How far it has got" testId="campaign-stages">
        <ul className="space-y-1.5">
          {c.stages.map((s) => (
            <li key={s.key} className="grid grid-cols-[12rem_3rem_1fr] items-center gap-3 text-sm" data-testid={`stage-${s.key}`}>
              <span className="text-slate-300">{s.label}</span>
              <span className="tabular-nums text-right">{s.count}</span>
              <span className="h-1.5 rounded bg-white/5 overflow-hidden">
                <span className="block h-full bg-accent/60" style={{ width: `${Math.min(100, (s.count / top) * 100)}%` }} />
              </span>
            </li>
          ))}
        </ul>
        <div className="text-[11px] text-slate-500 mt-2">Counted from what was logged against these people, not estimated.</div>
      </Card>

      <Card
        title={`People (${c.members.length})`}
        testId="campaign-members"
        aside={
          canWrite && fromPersona.length > 0 ? (
            <button className="btn-ghost text-xs" onClick={() => void addFromPersona()} data-testid="campaign-add-persona">
              Add the {fromPersona.length} from {c.personaName} not in it yet
            </button>
          ) : null
        }
      >
        {c.members.length === 0 ? (
          <div className="text-sm text-slate-500">Nobody yet. Pick a persona and add its people, or add people one at a time from their page.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Next step</th>
                <th>Last touch</th>
                <th className="text-right">Touches</th>
              </tr>
            </thead>
            <tbody>
              {c.members.map((m) => (
                <tr key={m.id} className="cursor-pointer" onClick={() => onOpenPerson(m.id)}>
                  <td>
                    <div className="text-sm">{m.name}</div>
                    <div className="text-[11px] text-slate-500">{m.company}</div>
                  </td>
                  <td className="text-xs text-slate-300">{m.step}</td>
                  <td className="text-xs text-slate-400">{when(m.lastTouchAt)}</td>
                  <td className="text-right tabular-nums">{m.touches}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Messaging for this campaign" testId="campaign-messaging">
        <div className="text-xs text-slate-400 mb-3">
          A starting shape, not a template to blast. Each person’s own messages (on their page) are what gets sent; these keep the campaign’s angle consistent.
        </div>
        <div className="space-y-3">
          <label className="block text-xs text-slate-400">
            Connection note shape
            <textarea className={`${inputCls} mt-1`} rows={2} value={v('noteTemplate')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, noteTemplate: e.target.value })} data-testid="campaign-note" />
            <StyleIssues issues={noteIssues?.filter((i) => i.rule !== 'no_name' && i.rule !== 'empty') ?? null} />
          </label>
          <label className="block text-xs text-slate-400">
            First message shape
            <textarea className={`${inputCls} mt-1`} rows={3} value={v('dmTemplate')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, dmTemplate: e.target.value })} data-testid="campaign-dm" />
            <StyleIssues issues={dmIssues?.filter((i) => i.rule !== 'no_name' && i.rule !== 'empty') ?? null} />
          </label>
          <div className="grid sm:grid-cols-[1fr_8rem] gap-2">
            <label className="block text-xs text-slate-400">
              Email subject
              <input className={`${inputCls} mt-1`} value={v('emailSubject')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, emailSubject: e.target.value })} />
            </label>
            <label className="block text-xs text-slate-400">
              Follow up after (days)
              <input type="number" min={1} max={90} className={`${inputCls} mt-1`} value={v('followUpDays')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, followUpDays: e.target.value === '' ? null : Number(e.target.value) })} />
            </label>
          </div>
          <label className="block text-xs text-slate-400">
            Email shape
            <textarea className={`${inputCls} mt-1`} rows={3} value={v('emailTemplate')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, emailTemplate: e.target.value })} />
          </label>
        </div>
      </Card>

      {canWrite && Object.keys(draft).length > 0 && (
        <div className="flex gap-2 sticky bottom-4">
          <button className="btn-primary" onClick={() => void save()} data-testid="campaign-save">
            Save campaign
          </button>
          <button className="btn-ghost" onClick={() => setDraft({})}>
            Discard changes
          </button>
        </div>
      )}

      <Card title={`Everything sent as part of it (${c.events.length})`} testId="campaign-events">
        {c.events.length === 0 ? (
          <div className="text-sm text-slate-500">Nothing logged against this campaign yet. Messages and emails sent from a person’s page while they are in it are recorded here.</div>
        ) : (
          <ol className="space-y-2">
            {c.events.map((e) => (
              <li key={e.id} className="text-sm border-l border-white/10 pl-3">
                <button className="text-slate-200 hover:text-accent" onClick={() => onOpenPerson(e.prospectId)}>
                  {e.prospectName}
                </button>
                <span className="text-slate-400">
                  {' '}
                  · {e.direction === 'in' ? 'they' : 'we'}: {TOUCH_CHANNEL_LABEL[e.channel] ?? e.channel}
                  {e.outcome ? `, ${TOUCH_OUTCOME_LABEL[e.outcome]}` : ''}
                </span>
                <div className="text-[11px] text-slate-500">
                  {when(e.occurredAt)}
                  {e.adminEmail ? ` · ${e.adminEmail}` : ''}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  )
}
